import React from 'react';
import cx from 'classnames';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import { Picker, Uploader, UploadTextAndButton, CSVUploadLoadingState } from 'components/File';
import { PRIVATE_UPLOAD } from './TargetByStudentIdUploadPageContainer';
import DropZoneModal from 'components/DropZoneModal';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import Label from 'components/Label';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Select from 'components/Select';
import TargetByStudentIdUploadPageSidebar from './TargetByStudentIdUploadPageSidebar';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import CurrentUser from 'auth/CurrentUser';

import type { ReactElement } from 'react';

const prefixEvent = (suffix: string) => `announcements.target_by_student_id.upload_list.${suffix}`;
function trackEvent(key: string, properties: any = {}) {
  EventTracking.trackKey(prefixEvent(key), properties);
}
function trackUploadErrorView() {
  trackEvent('upload_error.view');
}
function trackUploadClick() {
  trackEvent('upload_file.click');
}

type Props = {
  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'ComposerOrganization'.
  organizations: ComposerOrganization[] | undefined | null;
  selectedOrganizationUuid: string;
  onOrgSelection: (organizationUuid: string) => void;
  csvIsMatching: boolean;
  csvMatchError?: Error | null;
  // from container
  onUploadSuccess: (file: any) => void;
  uploadFile: (file: any, onUploadProgress: Function, onUploadSuccess: Function) => void;
  // Drag 'n drop props
  connectDropTarget: Function;
  isDraggingFile?: boolean;
};

type State = {
  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'ComposerOrganization'.
  selectedOrganization: ComposerOrganization | undefined | null;
  csvIsLoading: boolean;
};

class TargetByStudentIdUploadPageBody extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      csvIsLoading: false,
      selectedOrganization: null,
    };
  }

  componentDidMount() {
    trackEvent('view');
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.isDraggingFile && this.props.isDraggingFile) {
      trackEvent('upload_file.drag');
    }
  }

  selectOrganization = (organizationUuid: string) => {
    const { organizations } = this.props;
    const selectedOrganization =
      organizations && organizations.find((s) => s.uuid === organizationUuid);

    if (!selectedOrganization) {
      ToastActions.showErrorToast(
        'Oops! Looks like something went wrong. Please select another organization.'
      );
      console.error('TBSI - tried to select a nonexistent organization');
      return;
    }

    trackEvent('org_dropdown_item.click', { orgUuid: organizationUuid });
    this.props.onOrgSelection(organizationUuid);
    this.setState({ selectedOrganization });
  };

  renderDropzoneModal = (): ReactElement<any> | undefined | null => {
    const { isDraggingFile } = this.props;
    if (!isDraggingFile) {
      return null;
    }

    return (
      <div>
        <DropZoneModal titleDrop="Drop a CSV file here" isHovering />
      </div>
    );
  };

  renderMain = () => {
    const { organizations, selectedOrganizationUuid, csvIsMatching, csvMatchError } = this.props;
    const { csvIsLoading, selectedOrganization } = this.state;
    const organizationsWithPermissions = organizations
      ? organizations.filter((organization) => organization.permissions.canSendToStudentIdList)
      : [];
    const districtOptions = organizationsWithPermissions
      .filter((organization) => organization.type === 'District')
      .map((organization) => {
        return { label: organization.name, value: organization.uuid };
      });
    const schoolOptions = organizationsWithPermissions
      .filter((organization) => organization.type === 'School')
      .map((organization) => {
        return { label: organization.name, value: organization.uuid };
      });
    const organizationOptions = [
      { label: 'Districts', options: districtOptions },
      { label: 'Schools', options: schoolOptions },
    ];
    const loadingText = csvIsMatching
      ? 'Matching student numbers to students on Remind...'
      : 'Uploading your CSV file...';
    const selectedOrganizationName = selectedOrganization && selectedOrganization.name;
    const percentOfStudentsAtSelectedOrganizationWithIds: number | undefined | null =
      selectedOrganization && Math.floor(selectedOrganization.studentIdPercentage * 100);
    const currentUser = CurrentUser.getUser();
    const featureFlagEnabled =
      currentUser &&
      currentUser.hasFeature('2022.feb.message_builder_v1_csv_students_list_by_district');

    return (
      <div className="TargetByStudentIdUploadPage-main">
        <Row size="small">
          <Label>{featureFlagEnabled ? 'Organization' : 'School'}</Label>
          <Select
            autoFocus
            value={this.props.selectedOrganizationUuid}
            options={organizationOptions}
            placeholder={featureFlagEnabled ? 'Choose an organization' : 'Choose a school'}
            onChange={(organizationUuid) => this.selectOrganization(organizationUuid)}
            onMenuOpen={() => trackEvent('org_dropdown.view')}
          />
          {selectedOrganizationName &&
            typeof percentOfStudentsAtSelectedOrganizationWithIds === 'number' &&
            percentOfStudentsAtSelectedOrganizationWithIds < 50 && (
              <MediaObject className="TargetByStudentIdUploadPage-fieldError" gutter="xsmall">
                <Glyph name="caution" size="xsmall" color="pencil" />
                <Text color="chalkboard">
                  {percentOfStudentsAtSelectedOrganizationWithIds}% of students at{' '}
                  {selectedOrganizationName} have student numbers on Remind. You’ll only be able to
                  message students with student numbers.{' '}
                  <Link
                    href={LinkConstants.UNMATCHED_STUDENTS}
                    target="_blank"
                    eventKey={prefixEvent('org_dropdown_cta.click')}
                  >
                    Learn more here
                  </Link>
                </Text>
              </MediaObject>
            )}
        </Row>
        <div
          className={cx('TargetByStudentIdUploadPage-dropzone', {
            'TargetByStudentIdUploadPage-dropzone--disabled': !selectedOrganizationUuid,
          })}
        >
          {this.renderDropzoneModal()}
          {csvIsMatching || csvIsLoading ? (
            <CSVUploadLoadingState text={loadingText} longListWarning={true} />
          ) : (
            <Picker acceptedFileTypes=".csv" data-testid="csv-uploader">
              {({ pick, file }) => {
                if (file) {
                  return (
                    <Uploader
                      file={file}
                      onUploadSuccess={this.props.onUploadSuccess}
                      private={PRIVATE_UPLOAD}
                    >
                      {() => <CSVUploadLoadingState text={loadingText} longListWarning={true} />}
                    </Uploader>
                  );
                }
                if (csvMatchError) {
                  return (
                    <UploadTextAndButton
                      onClick={trackUploadClick}
                      onErrorView={trackUploadErrorView}
                      size="small"
                      pick={pick}
                      error={csvMatchError.message}
                    />
                  );
                }
                return (
                  <UploadTextAndButton
                    onClick={trackUploadClick}
                    onErrorView={trackUploadErrorView}
                    size="large"
                    pick={pick}
                  />
                );
              }}
            </Picker>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { connectDropTarget } = this.props;

    return (
      <React.Fragment>
        <TargetByStudentIdUploadPageSidebar />
        {this.props.selectedOrganizationUuid
          ? connectDropTarget(this.renderMain())
          : this.renderMain()}
      </React.Fragment>
    );
  }
}

const dndEvents = {
  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
  drop: (props, monitor, component) => {
    const { files } = monitor.getItem();
    if (files.length > 1) {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to upload more than one file - please try again'
      );
      return;
    }
    const file = files[0];
    const onUploadProgress = () => {
      component.setState({
        csvIsLoading: true,
      });
    };
    // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'data' implicitly has an 'any' type.
    const onUploadSuccess = (data) => {
      if (props.onUploadSuccess) {
        props.onUploadSuccess(data);
      }
      component.setState({
        csvIsLoading: false,
      });
    };
    props.uploadFile(file, onUploadProgress, onUploadSuccess);
  },
  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
  canDrop: (props) => {
    return !props.csvIsMatching; // can only attach one file at a time
  },
};

// @ts-expect-error ts-migrate(7006) FIXME: Parameter 'connector' implicitly has an 'any' type... Remove this comment to see the full error message
const collect = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const withDropTarget = DropTarget(NativeTypes.FILE, dndEvents, collect);

export { TargetByStudentIdUploadPageBody };

export default withDropTarget(TargetByStudentIdUploadPageBody);
